import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Car, CalendarDays, CheckCircle2, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import VeriffVerification from "@/components/booking/VeriffVerification";

export default function VerifyDriver() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/veriff/driver/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "This verification link is invalid or has expired."));
  }, [token]);

  const verified = done || info?.status === "verified";

  return (
    <div data-testid="verify-driver-page" className="min-h-[80vh] bg-slate-50 flex items-center justify-center px-5 py-28">
      <Seo title="Driver Identity Verification | Belo Rentals" description="Complete your identity verification as an additional driver for a Belo Rentals trip." noindex />
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-card p-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-blue-50 text-blue-600"><ShieldCheck size={20} /></span>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">{t("veriff.driverPageTitle")}</h1>
        </div>

        {err && <p data-testid="verify-driver-error" className="mt-5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{err}</p>}

        {info && (
          <>
            <p className="text-slate-500 text-sm mt-3">{t("veriff.driverPageIntro", { customer: info.customer_first_name })}</p>
            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-2 text-sm">
              <p className="flex items-center gap-2 text-slate-700 font-semibold"><Car size={15} className="text-blue-600" /> {info.car_name}</p>
              <p className="flex items-center gap-2 text-slate-500"><CalendarDays size={15} className="text-blue-600" /> {info.pickup_date} → {info.return_date}</p>
              <p className="text-slate-500">{t("veriff.driverLabel")}: <span className="font-semibold text-slate-800">{info.driver_name}</span></p>
            </div>

            {verified ? (
              <div data-testid="driver-verified" className="mt-6 flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-4">
                <CheckCircle2 size={20} className="text-green-600 shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">{t("veriff.driverVerified")}</p>
              </div>
            ) : info.status === "failed" ? (
              <div className="mt-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-4">
                <AlertTriangle size={20} className="text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{t("veriff.declined")}</p>
              </div>
            ) : (
              <>
                <p className="text-slate-500 text-sm mt-5">{t("veriff.intro")}</p>
                <button onClick={() => setRunning(true)} data-testid="driver-start-verification" className="mt-5 w-full bg-blue-600 text-white py-3.5 rounded-full font-semibold hover:bg-blue-700 transition-colors">
                  {t("veriff.start")}
                </button>
              </>
            )}
          </>
        )}
      </div>

      {running && (
        <VeriffVerification
          createSession={() => api.post(`/veriff/driver/${token}/session`).then((r) => r.data)}
          pollToken={token}
          onVerified={() => { setDone(true); setRunning(false); }}
          onClose={() => setRunning(false)}
        />
      )}
    </div>
  );
}
